import React from "react";
import { json } from "@remix-run/node";
import { useLoaderData, useNavigation, Link } from "@remix-run/react";
import { RecipeService } from "~/services/recipe.server";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import type { RecipeIngredientDisplay } from "~/lib/types";
import { WrappedLazyRecipeDetail } from "~/lib/lazy-components";
// SEO 관련 import 제거 - 하이드레이션 에러 방지를 위해

// 메타태그 함수: SEO 최적화된 메타태그 생성
export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data || "error" in data) {
    return [
      { title: "레시피를 찾을 수 없습니다 - 슬로우다이브" },
      { name: "description", content: "요청하신 레시피를 찾을 수 없습니다." },
    ];
  }

  const { recipe } = data;
  const description =
    recipe.description ||
    `${recipe.title} 레시피를 통해 건강한 저속노화를 시작하세요. 조리시간 ${recipe.cookTime}분, ${recipe.servings}인분`;

  return [
    { title: `${recipe.title} - 슬로우다이브` },
    { name: "description", content: description },
    { property: "og:title", content: `${recipe.title} - 슬로우다이브` },
    { property: "og:description", content: description },
    { property: "og:type", content: "article" },
    ...(recipe.imageUrl
      ? [{ property: "og:image", content: recipe.imageUrl }]
      : []),
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: `${recipe.title} - 슬로우다이브` },
    { name: "twitter:description", content: description },
  ];
};

// 로더 함수: 특정 ID의 레시피 데이터를 가져옴
export async function loader({ params, request }: LoaderFunctionArgs) {
  try {
    console.log("======== 단일 레시피 상세 페이지 로더 함수 시작 ========");
    console.log("요청 URL:", request.url);
    console.log("요청 메서드:", request.method);
    console.log("전체 파라미터:", params);

    // 파라미터 검증
    const { id } = params;
    console.log("레시피 ID:", id);

    if (!id) {
      console.error("레시피 ID가 없음 - 400 에러 반환");
      return json(
        {
          error: "레시피 ID가 필요합니다",
          message:
            "레시피 ID가 제공되지 않았습니다. 올바른 URL을 입력해주세요.",
        },
        { status: 400 }
      );
    }

    // UUID 형식 검증 (선택적)
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(id)) {
      console.error("잘못된 레시피 ID 형식:", id);
      return json(
        {
          error: "잘못된 레시피 ID 형식입니다",
          message: "제공된 레시피 ID가 올바른 형식이 아닙니다.",
        },
        { status: 400 }
      );
    }

    // 데이터베이스 조회 - 정규화된 재료와 함께 조회
    console.log("RecipeService.getRecipeWithIngredients 호출 시작");
    let recipeData;
    try {
      recipeData = await RecipeService.getRecipeWithIngredients(id);
      console.log("RecipeService.getRecipeWithIngredients 호출 완료");
    } catch (dbError) {
      console.error("데이터베이스 조회 오류:", dbError);
      return json(
        {
          error: "데이터베이스 오류",
          message:
            "레시피 정보를 조회하는 중 데이터베이스 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
        },
        { status: 500 }
      );
    }

    // 결과 검증
    if (recipeData?.recipe) {
      console.log(
        "레시피 찾음:",
        recipeData.recipe.id,
        recipeData.recipe.title
      );

      // 정규화된 재료 데이터를 레시피 객체에 통합
      const recipe = {
        ...recipeData.recipe,
        ingredients: recipeData.ingredients.map(
          (ing) =>
            ({
              name: ing.ingredient.name,
              quantity: ing.quantity,
              unit: ing.unit,
              prefix: ing.prefix || undefined,
              suffix: ing.suffix || undefined,
              notes: ing.notes || undefined,
              isOptional: ing.isOptional || false,
            } as RecipeIngredientDisplay)
        ),
      };

      console.log("레시피 데이터 반환, ID:", id);
      console.log("======== 단일 레시피 상세 페이지 로더 함수 종료 ========");
      return json({ recipe });
    } else {
      console.error("레시피를 찾을 수 없음, ID:", id);
      return json(
        {
          error: "레시피를 찾을 수 없습니다",
          message:
            "요청하신 레시피를 찾을 수 없습니다. 삭제되었거나 존재하지 않는 레시피입니다.",
        },
        { status: 404 }
      );
    }
  } catch (error) {
    console.error("레시피 조회 오류:", error);
    console.error(
      "스택 트레이스:",
      error instanceof Error ? error.stack : "스택 정보 없음"
    );

    // 더 자세한 오류 정보 제공
    const errorMessage =
      error instanceof Error ? error.message : "알 수 없는 오류";

    return json(
      {
        error: "레시피를 조회하는 중 오류가 발생했습니다",
        message:
          "서버에서 예기치 않은 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
        details: errorMessage,
      },
      { status: 500 }
    );
  }
}

// 레시피 상세 페이지 컴포넌트
export default function RecipeDetailPage() {
  const loaderData = useLoaderData<typeof loader>();
  const navigation = useNavigation();

  // 로딩 상태 확인
  const isLoading = navigation.state === "loading";

  // 데이터 상태 확인
  const hasError = "error" in loaderData;
  const hasMessage = "message" in loaderData;
  const hasDetails = "details" in loaderData;

  // 레시피 데이터
  const recipe = "recipe" in loaderData ? loaderData.recipe : null;

  // 오류 메시지와 디버깅 정보 (문자열로 변환)
  const isDevEnvironment =
    typeof process !== "undefined" && process.env.NODE_ENV === "development";
  const errorMessage = hasError ? String(loaderData.error || "") : "";
  const messageString = hasMessage ? String(loaderData.message || "") : "";
  const debugInfo = isDevEnvironment && hasDetails ? loaderData.details : null;
  const debugInfoString = debugInfo ? String(debugInfo) : "";

  // 로딩 상태 표시
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Link
            to="/recipes"
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <svg
              className="h-5 w-5 mr-1"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            레시피 목록으로 돌아가기
          </Link>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden p-6">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 dark:border-blue-400 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">
              레시피 정보를 불러오는 중입니다...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 dark:bg-red-900 dark:border-red-700 p-4 my-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400 dark:text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-400">
                {errorMessage || "레시피를 불러오는 중 오류가 발생했습니다."}
              </p>
              {hasMessage && (
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                  {messageString}
                </p>
              )}
              {isDevEnvironment && debugInfo ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded">
                  <strong>디버그 정보:</strong> {debugInfoString}
                </p>
              ) : null}
            </div>
          </div>
        </div>
        <Link
          to="/recipes"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          레시피 목록으로 돌아가기
        </Link>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 dark:bg-yellow-900 dark:border-yellow-700 p-4 my-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400 dark:text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                레시피를 찾을 수 없습니다.
              </p>
            </div>
          </div>
        </div>
        <Link
          to="/recipes"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          레시피 목록으로 돌아가기
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 뒤로 가기 링크 */}
      <div className="mb-6">
        <Link
          to="/recipes"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
        >
          <svg
            className="h-5 w-5 mr-1"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          레시피 목록으로 돌아가기
        </Link>
      </div>

      {/* 레시피 상세 정보 */}
      <WrappedLazyRecipeDetail
        recipe={{
          ...recipe,
          imageUrl: recipe.imageUrl || undefined,
          createdAt: new Date(recipe.createdAt),
          updatedAt: new Date(recipe.updatedAt),
          nutritionInfo: {
            calories: recipe.nutritionInfo?.calories ?? 0,
            protein: recipe.nutritionInfo?.protein ?? 0,
            carbs: recipe.nutritionInfo?.carbs ?? 0,
            fat: recipe.nutritionInfo?.fat ?? 0,
            fiber: recipe.nutritionInfo?.fiber ?? 0,
            sugar: recipe.nutritionInfo?.sugar ?? 0,
            sodium: recipe.nutritionInfo?.sodium ?? 0,
            potassium: recipe.nutritionInfo?.potassium ?? 0,
            calcium: recipe.nutritionInfo?.calcium ?? 0,
            iron: recipe.nutritionInfo?.iron ?? 0,
            vitaminC: recipe.nutritionInfo?.vitaminC ?? 0,
            antioxidants: recipe.nutritionInfo?.antioxidants ?? 0,
          },
        }}
      />
    </div>
  );
}
